/* eslint-disable max-len */
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Paper,
  Button,
  Grid,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  CircularProgress,
  Radio,
  RadioGroup,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from 'components/home';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useSnackbar } from 'notistack';
import { ContainerForm } from 'components/shared/container_form';
import { CicaafAPI } from 'api/cicaaf/cicaaf.api';
import { mascaraCpfCnpj } from 'utils/format_cpf';
import { mascaraTelefone } from 'utils/format_phone';
import { PictureAsPdf } from '@mui/icons-material';
import DocumentAPI from 'api/document/document';
import {
  determineFileType,
  isInvalidEmail as helpersIsInvalidEmail,
  validateForm,
  buildPayload,
} from './helpers';
import { validarCPF, validarCNPJ } from 'utils/uteis';

const ORGAOS_EMISSORES_ALVARA = [
  { value: 'SECRETARIA SAÚDE MUNICÍPIO', label: 'Secretaria Saúde Município' },
  { value: 'SECRETARIA SAÚDE ESTADO', label: 'Secretaria Saúde Estado' },
];

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

interface Product {
  uuid: string;
  name: string;
  type: string;
}

interface CollectionProduct {
  uuid: string;
  commercialized_value: number;
  commercialized_volume: number;
  measurement_unit: string;
  product: Product;
}

interface Institution {
  email?: string;
}

interface FormData {
  uuid: string;
  reference_year: number;
  entity_name: string;
  entity_address: string;
  institution?: Institution;
  president_responsible_name: string;
  entity_type: string;
  location_type: string;
  cnpj_status: string;
  belongs_to_association_cooperative: boolean;
  parent_association_cooperative_uuid: string;
  latitude: number | string;
  longitude: number | string;
  total_members: number;
  female_members: number;
  indigenous_members: number;
  young_members: number;
  observations: string;
  collectionProducts: CollectionProduct[];
  informant_name: string;
  informant_document: string;
  informant_phone: string;
  informant_email?: string;
  president_name?: string;
  identification?: string;
  createdAt?: string;
  rendaCredito?: any;
  [key: string]: any;
}

export function formatDatePtBR(dateInput?: string | null, fallback = ''): string {
  if (!dateInput) return fallback;
  const d = new Date(dateInput);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString('pt-BR');
}

export function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

export enum CicaafCnpjStatusEnum {
  ACTIVE = 'ATIVA',
  SUSPENDED = 'SUSPENSA',
  UNFIT = 'INAPTA',
  ACTIVE_NOT_REGULAR = 'ATIVA NÃO REGULAR',
  CLOSED = 'BAIXADA',
  NULL = 'NULA',
}


export const GerenciamentoEditCicaaf: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [status, setStatus] = useState<string>('');
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [cpfError, setCpfError] = useState<string>('');
  const [identificationError, setIdentificationError] = useState<string>('');

  const validateIdentification = (identification: string, updateError = true): boolean => {
    if (!identification) {
      if (updateError) setIdentificationError('');
      return true; // Não é obrigatório, mas se preenchido deve ser válido
    }

    const cleaned = identification.replace(/\D/g, '');

    if (cleaned.length === 11) {
      const isValid = validarCPF(identification);
      if (!isValid) {
        if (updateError) setIdentificationError('CPF inválido. Os dígitos verificadores não conferem.');
        return false;
      }
    } else if (cleaned.length === 14) {
      const isValid = validarCNPJ(identification);
      if (!isValid) {
        if (updateError) setIdentificationError('CNPJ inválido. Os dígitos verificadores não conferem.');
        return false;
      }
    } else {
      if (updateError) setIdentificationError('CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos.');
      return false;
    }

    if (updateError) setIdentificationError('');
    return true;
  };

  const validateCpf = (cpf: string, updateError = true): boolean => {
    if (!cpf) {
      if (updateError) setCpfError('');
      return false;
    }

    const cleanedCpf = cpf.replace(/\D/g, '');

    if (cleanedCpf.length !== 11) {
      if (updateError) setCpfError('CPF deve ter 11 dígitos');
      return false;
    }

    const isValid = validarCPF(cpf);
    if (!isValid) {
      if (updateError) setCpfError('CPF inválido. Os dígitos verificadores não conferem.');
      return false;
    }

    if (updateError) setCpfError('');
    return true;
  };

  const isFormValid = (): boolean => {
    if (!formData) return false;

    const obrigatorios = [
      formData.entity_name,
      formData.entity_type,
      formData.cnpj_status,
      formData.latitude,
      formData.longitude,
      formData.informant_name,
      formData.informant_document,
      formData.informant_phone,
    ];

    const todosPreenchidos = obrigatorios.every(
      (campo) => campo !== null && campo !== undefined && campo !== ''
    );

    const produtosValidos =
      formData.collectionProducts.length === 0 ||
      (Array.isArray(formData.collectionProducts) &&
        formData.collectionProducts.every((p) => {
          const valor =
            typeof p.commercialized_volume === 'string'
              ? parseFloat((p.commercialized_volume as string).replace(',', '.'))
              : p.commercialized_volume;

          return (
            p.product?.name &&
            p.product?.type &&
            Number.isFinite(valor) &&
            valor > 0
          );
        }));

    const cnpjStatusValido = Object.values(CicaafCnpjStatusEnum).includes(formData.cnpj_status as CicaafCnpjStatusEnum);

    const cpfValido = formData.informant_document
      ? validateCpf(formData.informant_document, false)
      : true;

    const identificationValido = formData.identification
      ? validateIdentification(formData.identification, false)
      : true;

    return todosPreenchidos && produtosValidos && cnpjStatusValido && cpfValido && identificationValido;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (uuid) {
          const response = await CicaafAPI.getCicaafCollection(uuid);
          if (response.Status === 'OK') {
            const initialData = response.dados;

            if (!initialData.cnpj_status) {
              initialData.cnpj_status = CicaafCnpjStatusEnum.ACTIVE;
            }

            const defaultProduction = {
              products: [],
              vegetal_license: { has_sanitary_license: false, license_number: '', issuing_agency: '', issue_date: '' },
              animal_inspection: { has_inspection_register: false, inspection_seals: [] as any[] },
            };
            initialData.productionCommercialization = { ...defaultProduction };

            setFormData(initialData);
            setStatus(initialData.status);

            if (initialData.collectionProducts && initialData.collectionProducts.length > 0) {
              const firstProductUuid = initialData.collectionProducts[0]?.product?.uuid;
              setSelectedProduct(firstProductUuid);
            }

            const prodResp = await CicaafAPI.getProductionComercialization(uuid);
            if (prodResp?.Status === 'OK' && prodResp.dados) {
              const d = prodResp.dados;
              setFormData((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  productionCommercialization: {
                    products: d.products ?? prev.productionCommercialization?.products ?? [],
                    vegetal_license: {
                      has_sanitary_license: d.vegetal_license?.has_sanitary_license ?? false,
                      license_number: d.vegetal_license?.license_number ?? '',
                      issuing_agency: d.vegetal_license?.issuing_agency ?? '',
                      issue_date: d.vegetal_license?.issue_date ?? '',
                    },
                    animal_inspection: {
                      has_inspection_register: d.animal_inspection?.has_inspection_register ?? false,
                      inspection_seals: d.animal_inspection?.inspection_seals ?? [],
                    },
                  },
                };
              });
            }
          } else {
            console.error('Erro da API:', response.Mensagem);
          }
        }
      } catch (error) {
        console.error('Erro ao buscar dados da coleta:', error);
        enqueueSnackbar('Erro ao buscar dados da coleta.', { variant: 'error' });
      }
    };

    fetchData();
  }, [uuid]);




  const fetchProducts = async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const products = await CicaafAPI.listAllProducts();
      setAvailableProducts((prev) => [...prev, ...products]);
      setHasMore(products.length === 49);
    } catch (error) {
      console.error('Erro ao carregar produtos', error);
    } finally {
      setLoading(false);
    }
  };






  useEffect(() => {
    fetchProducts();
  }, [page]);



  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: string,
  ) => {
    if (event.target.files) {
      const file = event.target.files[0];

      const reader = new FileReader();

      reader.onloadend = () => {
        if (formData) {
          const fileType = determineFileType(field);

          DocumentAPI.upload({
            file: file,
            documentInfo: {
              type: fileType,
            },
          })
            .then((response) => {
              const fileUuid = response.uuid;

              setFormData((prevFormData) => {
                if (!prevFormData) return prevFormData;

                return {
                  ...prevFormData,
                  [field]: {
                    uuid: fileUuid,
                    name: file.name,
                    size: file.size,
                  },
                };
              });
            })
            .catch((error: Error) => {
              console.error('Erro no upload do arquivo:', error);
            });
        }
      };

      if (file) {
        reader.readAsDataURL(file);
      }
    }
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleSave = async () => {
    try {
      if (!formData?.uuid) {
        throw new Error('UUID não encontrado');
      }

      const uuid = formData.uuid;

      const payload = {
        uuid: formData.uuid,
        identification: formData.identification,
        reference_year: formData.reference_year,
        entity_name: formData.entity_name,
        entity_address: formData.entity_address,
        entity_email: formData.institution?.email,
        president_responsible_name: formData.president_responsible_name,
        entity_type: formData.entity_type,
        location_type: formData.location_type,
        cnpj_status: formData.cnpj_status,
        belongs_to_association_cooperative:
          formData.belongs_to_association_cooperative,
        parent_association_cooperative_uuid:
          formData.parent_association_cooperative_uuid,
        latitude: Number(formData.latitude),
        longitude: Number(formData.longitude),
        informant_name: formData.informant_name,
        informant_document: formData.informant_document,
        informant_phone: formData.informant_phone,
        informant_email: formData.informant_email,
        president_name: formData.president_name,
        total_members: formData.total_members,
        female_members: formData.female_members,
        indigenous_members: formData.indigenous_members,
        young_members: formData.young_members,
        observations: formData.observations,
        collectionProducts: formData.collectionProducts?.map((item) => ({
          uuid: item.uuid,
          commercialized_value: item.commercialized_value,
          commercialized_volume: item.commercialized_volume,
          measurement_unit: item.measurement_unit,
          product: {
            uuid: item.product?.uuid,
            name: item.product?.name,
            type: item.product?.type,
          },
        })),
        statute_document: formData.statute_document?.uuid,
        election_act_document: formData.election_act_document?.uuid,
      };

      const response = await CicaafAPI.updateCicaafCollection(uuid, payload);

      if (response?.Status !== 'OK') {
        enqueueSnackbar(
          'Erro ao salvar alterações: ' +
          (response?.Mensagem ?? 'Erro desconhecido'),
          { variant: 'error' },
        );
        return;
      }

      if (formData.productionCommercialization) {
        const prodPayload = {
          products: (formData.collectionProducts ?? [])
            .filter((p: any) => p.product?.uuid)
            .map((p: any) => ({
              product_uuid: p.product.uuid,
              commercialized_value: Number(p.commercialized_value) ?? 0,
              commercialized_volume: Number(p.commercialized_volume) ?? 0,
              measurement_unit: p.measurement_unit ?? 'KG',
            })),
          vegetal_license: {
            has_sanitary_license: formData.productionCommercialization.vegetal_license?.has_sanitary_license ?? false,
            license_number: formData.productionCommercialization.vegetal_license?.license_number || undefined,
            issuing_agency: formData.productionCommercialization.vegetal_license?.issuing_agency || undefined,
            issue_date: formData.productionCommercialization.vegetal_license?.issue_date || undefined,
          },
          animal_inspection: {
            has_inspection_register: formData.productionCommercialization.animal_inspection?.has_inspection_register ?? false,
            inspection_seals: formData.productionCommercialization.animal_inspection?.inspection_seals ?? [],
          },
        };
        const prodResponse = await CicaafAPI.updateProductionComercialization(uuid, prodPayload);
        if (prodResponse?.Status !== 'OK') {
          enqueueSnackbar(
            'Falha ao salvar produção/comercialização (alvará, selos): ' + (prodResponse?.Mensagem ?? 'Verifique os campos obrigatórios.'),
            { variant: 'error' },
          );
          return;
        }
      }

      enqueueSnackbar('Alterações salvas com sucesso!', { variant: 'success' });
      navigate(-1);
    } catch (error) {
      console.error('Erro ao salvar alterações:', error);
      enqueueSnackbar('Erro ao conectar com o servidor.', { variant: 'error' });
    }
  };

  const isInvalidEmail = helpersIsInvalidEmail(formData?.informant_email);

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title='Edição do Formulário CICAAF' showBackButton={true} />
      <Box
        width='97%'
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
        margin='2rem'
      >
        <ContainerForm title='Edição da Coleta' width='100%'>
          <Paper elevation={2}>
            <Tabs
              value={tabIndex}
              onChange={handleChange}
              variant='fullWidth'
              indicatorColor='primary'
              textColor='primary'
            >
              <Tab label='Identificação' />
              <Tab label='Produção' />
              <Tab label='Dados Informante' />
              <Tab label='Observação' />
            </Tabs>
            {formData && (
              <TabPanel value={tabIndex} index={0}>
                <Typography variant='h6' gutterBottom>
                  Dados de Identificação
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label='CNPJ/CPF'
                      fullWidth
                      value={mascaraCpfCnpj(formData.identification ?? '')}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/\D/g, '');
                        setFormData({
                          ...formData,
                          identification: cleaned,
                        });
                        // Limpar erro ao digitar
                        if (identificationError) {
                          setIdentificationError('');
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value) {
                          validateIdentification(value);
                        }
                      }}
                      error={!!identificationError}
                      helperText={identificationError}
                      inputProps={{ maxLength: 18 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label='Nome da Entidade'
                      fullWidth
                      required
                      error={!formData.entity_name}
                      helperText={
                        !formData.entity_name ? 'Campo obrigatório' : ''
                      }
                      value={formData.entity_name?.toUpperCase() || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          entity_name: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel id='entity-type-label'>
                        Tipo Entidade
                      </InputLabel>
                      <Select
                        labelId='entity-type-label'
                        id='entity-type'
                        label='Tipo Entidade'
                        value={formData.entity_type ?? ''}
                        error={!formData.entity_type}
                        displayEmpty
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            entity_type: e.target.value,
                          })
                        }
                        sx={{ textTransform: 'uppercase' }}
                        renderValue={(selected) =>
                          selected
                            ? selected.toUpperCase()
                            : 'Selecione o tipo de entidade'
                        }
                      >
                        <MenuItem value='' disabled>
                          Selecione o tipo de entidade
                        </MenuItem>
                        <MenuItem value='COOPERATIVA'>COOPERATIVA</MenuItem>
                        <MenuItem value='ASSOCIAÇÃO'>ASSOCIAÇÃO</MenuItem>
                        <MenuItem value='AGROINDÚSTRIA'>AGROINDÚSTRIA</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel id='status-funcionamento-label'>
                        Status de Funcionamento
                      </InputLabel>
                      <Select
                        labelId='status-funcionamento-label'
                        id='cnpj-status'
                        value={formData.cnpj_status || CicaafCnpjStatusEnum.ACTIVE}
                        label='Status de Funcionamento'
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            cnpj_status: e.target.value,
                          })
                        }
                        sx={{ textTransform: 'uppercase' }}
                        renderValue={(selected) =>
                          selected ? selected.toUpperCase() : 'Selecione o status'
                        }
                      >
                        <MenuItem value='' disabled>
                          Selecione o status
                        </MenuItem>
                        <MenuItem value={CicaafCnpjStatusEnum.ACTIVE}>
                          {CicaafCnpjStatusEnum.ACTIVE}
                        </MenuItem>
                        <MenuItem value={CicaafCnpjStatusEnum.SUSPENDED}>
                          {CicaafCnpjStatusEnum.SUSPENDED}
                        </MenuItem>
                        <MenuItem value={CicaafCnpjStatusEnum.UNFIT}>
                          {CicaafCnpjStatusEnum.UNFIT}
                        </MenuItem>
                        <MenuItem value={CicaafCnpjStatusEnum.ACTIVE_NOT_REGULAR}>
                          {CicaafCnpjStatusEnum.ACTIVE_NOT_REGULAR}
                        </MenuItem>
                        <MenuItem value={CicaafCnpjStatusEnum.CLOSED}>
                          {CicaafCnpjStatusEnum.CLOSED}
                        </MenuItem>
                        <MenuItem value={CicaafCnpjStatusEnum.NULL}>
                          {CicaafCnpjStatusEnum.NULL}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label='Ano de referência'
                      fullWidth
                      type='number'
                      value={formData.reference_year ?? ''}
                      onChange={(e) => {
                        const year = parseInt(e.target.value);
                        if (!isNaN(year) && year >= 2020 && year <= 2030) {
                          setFormData({
                            ...formData,
                            reference_year: year,
                          });
                        }
                      }}
                      inputProps={{ min: 2020, max: 2030 }}
                      helperText='Ano entre 2020 e 2030'
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant='body1' gutterBottom>
                      <strong>Substituir documentos:</strong>
                    </Typography>

                    <Box display='flex' justifyContent='space-between'>
                      {/* Estatuto Social */}
                      <Button
                        variant='contained'
                        color='primary'
                        component='label'
                        startIcon={<PictureAsPdf />}
                        sx={{ mb: 2, width: '48%', fontSize: '0.875rem' }}
                      >
                        Substituir Estatuto Social
                        <input
                          type='file'
                          accept='application/pdf'
                          hidden
                          onChange={(e) =>
                            handleFileChange(e, 'statute_document')
                          }
                        />
                      </Button>

                      {/* Ata de Eleição */}
                      <Button
                        variant='contained'
                        color='primary'
                        component='label'
                        startIcon={<PictureAsPdf />}
                        sx={{ mb: 2, width: '48%', fontSize: '0.875rem' }}
                      >
                        Substituir Ata de Eleição
                        <input
                          type='file'
                          accept='application/pdf'
                          hidden
                          onChange={(e) =>
                            handleFileChange(e, 'election_act_document')
                          }
                        />
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </TabPanel>
            )}

            {formData && (
              <>
                <TabPanel value={tabIndex} index={1}>
                  <Typography variant='h6' gutterBottom>
                    Dados de Produção
                  </Typography>

                  <Grid container spacing={2}>
                    {Array.isArray(formData.collectionProducts) &&
                      formData.collectionProducts.map(
                        (p: any, index: number) => (
                          <Grid item xs={12} md={6} key={p.uuid || index}>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                              <InputLabel id={`produto-label-${index}`} shrink={true}>
                                {`Cultura Principal 0${index + 1}`}
                              </InputLabel>

                              <Select
                                value={selectedProduct}
                                onChange={(e) => {
                                  const productUuid = e.target.value;

                                  setSelectedProduct(productUuid);

                                  const updatedProducts = [...formData.collectionProducts];
                                  updatedProducts[index] = {
                                    ...updatedProducts[index],
                                    product: availableProducts.find((prod) => prod.uuid === productUuid),
                                  };

                                  setFormData({
                                    ...formData,
                                    collectionProducts: updatedProducts,
                                  });
                                }}
                                label="Selecione o produto"
                                fullWidth
                              >
                                <MenuItem value=""><em>Selecione um produto</em></MenuItem>

                                {loading ? (
                                  <MenuItem disabled>
                                    <CircularProgress size={24} />
                                  </MenuItem>
                                ) : (
                                  availableProducts.length > 0 ? (
                                    availableProducts.map((prod) => (
                                      <MenuItem
                                        key={prod.uuid}
                                        value={prod.uuid}
                                        disabled={formData.collectionProducts.some((p: any) => p.product?.uuid === prod.uuid)}
                                      >
                                        {prod.name} ({prod.type})
                                      </MenuItem>
                                    ))
                                  ) : (
                                    <MenuItem disabled><em>Nenhum produto disponível</em></MenuItem>
                                  )
                                )}
                              </Select>

                            </FormControl>

                            <TextField
                              required
                              label={`Produtividade 0${index + 1}`}
                              value={
                                p.commercialized_volume
                                  ? `${Number(p.commercialized_volume).toLocaleString('pt-BR', {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}`
                                  : ''
                              }
                              onChange={(e) => {
                                const input = e.target.value.replace(/\D/g, '');

                                const updatedProducts = [...formData.collectionProducts];
                                updatedProducts[index] = {
                                  ...updatedProducts[index],
                                  commercialized_volume: parseFloat(input),
                                  measurement_unit: 'KG',
                                };

                                setFormData({
                                  ...formData,
                                  collectionProducts: updatedProducts,
                                });
                              }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position='end'>
                                    KG
                                  </InputAdornment>
                                ),
                              }}
                              fullWidth
                            />


                          </Grid>
                        ),
                      )}
                  </Grid>

                  {formData?.productionCommercialization && (() => {
                    const hasVegetalProduct = (formData.collectionProducts ?? []).some(
                      (p: any) => !String(p.product?.type ?? '').toUpperCase().includes('ANIMAL')
                    );
                    const hasAnimalProduct = (formData.collectionProducts ?? []).some(
                      (p: any) => String(p.product?.type ?? '').toUpperCase().includes('ANIMAL')
                    );
                    return (
                      <>
                  {hasVegetalProduct && (
                    <Box mt={4}>
                      <Typography sx={{ marginBottom: '20px' }} variant="subtitle1" fontWeight="bold" gutterBottom>
                        Informações Sanitárias - Produto Vegetal
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControl component="fieldset">
                            <FormLabel component="legend">Possui alvará sanitário</FormLabel>
                            <RadioGroup
                              row
                              value={formData.productionCommercialization.vegetal_license?.has_sanitary_license ? 'sim' : 'nao'}
                              onChange={(e) => {
                                const has = e.target.value === 'sim';
                                setFormData((prev) => {
                                  if (!prev?.productionCommercialization) return prev;
                                  return {
                                    ...prev,
                                    productionCommercialization: {
                                      ...prev.productionCommercialization,
                                      vegetal_license: {
                                        ...prev.productionCommercialization.vegetal_license,
                                        has_sanitary_license: has,
                                        license_number: prev.productionCommercialization.vegetal_license?.license_number ?? '',
                                        issuing_agency: prev.productionCommercialization.vegetal_license?.issuing_agency ?? '',
                                        issue_date: prev.productionCommercialization.vegetal_license?.issue_date ?? '',
                                      },
                                    },
                                  };
                                });
                              }}
                            >
                              <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                              <FormControlLabel value="nao" control={<Radio />} label="Não" />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                        {formData.productionCommercialization.vegetal_license?.has_sanitary_license && (
                          <>
                            <Grid item xs={12} md={4}>
                              <TextField
                                label="Número do registro"
                                value={formData.productionCommercialization.vegetal_license.license_number ?? ''}
                                onChange={(e) => setFormData((prev) => {
                                  if (!prev?.productionCommercialization?.vegetal_license) return prev;
                                  return {
                                    ...prev,
                                    productionCommercialization: {
                                      ...prev.productionCommercialization,
                                      vegetal_license: { ...prev.productionCommercialization.vegetal_license, license_number: e.target.value },
                                    },
                                  };
                                })}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <FormControl fullWidth>
                                <InputLabel id="orgao-emissor-alvara-label">Órgão emissor</InputLabel>
                                <Select
                                  labelId="orgao-emissor-alvara-label"
                                  label="Órgão emissor"
                                  value={formData.productionCommercialization.vegetal_license.issuing_agency ?? ''}
                                  onChange={(e) => setFormData((prev) => {
                                    if (!prev?.productionCommercialization?.vegetal_license) return prev;
                                    return {
                                      ...prev,
                                      productionCommercialization: {
                                        ...prev.productionCommercialization,
                                        vegetal_license: { ...prev.productionCommercialization.vegetal_license, issuing_agency: e.target.value },
                                      },
                                    };
                                  })}
                                >
                                  <MenuItem value="">
                                    <em>Selecione o órgão</em>
                                  </MenuItem>
                                  {ORGAOS_EMISSORES_ALVARA.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                label="Data de emissão (YYYY-MM-DD)"
                                type="date"
                                value={formData.productionCommercialization.vegetal_license.issue_date?.toString().slice(0, 10) ?? ''}
                                onChange={(e) => setFormData((prev) => {
                                  if (!prev?.productionCommercialization?.vegetal_license) return prev;
                                  return {
                                    ...prev,
                                    productionCommercialization: {
                                      ...prev.productionCommercialization,
                                      vegetal_license: { ...prev.productionCommercialization.vegetal_license, issue_date: e.target.value || undefined },
                                    },
                                  };
                                })}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Box>
                  )}

                  {hasAnimalProduct && (
                    <Box mt={4}>
                      <Typography sx={{ marginBottom: '20px' }} variant="subtitle1" fontWeight="bold" gutterBottom>
                        Informações Sanitárias - Produto Animal
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControl component="fieldset">
                            <FormLabel component="legend">Possui registro de inspeção</FormLabel>
                            <RadioGroup
                              row
                              value={formData.productionCommercialization.animal_inspection?.has_inspection_register ? 'sim' : 'nao'}
                              onChange={(e) => {
                                const has = e.target.value === 'sim';
                                setFormData((prev) => {
                                  if (!prev?.productionCommercialization) return prev;
                                  return {
                                    ...prev,
                                    productionCommercialization: {
                                      ...prev.productionCommercialization,
                                      animal_inspection: {
                                        ...prev.productionCommercialization.animal_inspection,
                                        has_inspection_register: has,
                                        inspection_seals: prev.productionCommercialization.animal_inspection?.inspection_seals ?? [],
                                      },
                                    },
                                  };
                                });
                              }}
                            >
                              <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                              <FormControlLabel value="nao" control={<Radio />} label="Não" />
                            </RadioGroup>
                          </FormControl>
                        </Grid>
                        {formData.productionCommercialization.animal_inspection?.has_inspection_register &&
                          (formData.productionCommercialization.animal_inspection.inspection_seals ?? []).map(
                            (selo: any, seloIndex: number) => (
                              <Grid container item xs={12} key={seloIndex} spacing={2}>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    label="Selo de inspeção"
                                    value={selo.inspection_seal ?? ''}
                                    onChange={(e) => {
                                      setFormData((prev) => {
                                        if (!prev?.productionCommercialization?.animal_inspection) return prev;
                                        const newSeals = [...(prev.productionCommercialization.animal_inspection.inspection_seals ?? [])];
                                        newSeals[seloIndex] = { ...newSeals[seloIndex], inspection_seal: e.target.value };
                                        return {
                                          ...prev,
                                          productionCommercialization: {
                                            ...prev.productionCommercialization,
                                            animal_inspection: { ...prev.productionCommercialization.animal_inspection!, inspection_seals: newSeals },
                                          },
                                        };
                                      });
                                    }}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    label="Número de registro"
                                    value={selo.register_number ?? ''}
                                    onChange={(e) => {
                                      setFormData((prev) => {
                                        if (!prev?.productionCommercialization?.animal_inspection) return prev;
                                        const newSeals = [...(prev.productionCommercialization.animal_inspection.inspection_seals ?? [])];
                                        newSeals[seloIndex] = { ...newSeals[seloIndex], register_number: e.target.value };
                                        return {
                                          ...prev,
                                          productionCommercialization: {
                                            ...prev.productionCommercialization,
                                            animal_inspection: { ...prev.productionCommercialization.animal_inspection!, inspection_seals: newSeals },
                                          },
                                        };
                                      });
                                    }}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField
                                    label="Data de emissão"
                                    type="date"
                                    value={selo.issue_date?.toString().slice(0, 10) ?? ''}
                                    onChange={(e) => {
                                      setFormData((prev) => {
                                        if (!prev?.productionCommercialization?.animal_inspection) return prev;
                                        const newSeals = [...(prev.productionCommercialization.animal_inspection.inspection_seals ?? [])];
                                        newSeals[seloIndex] = { ...newSeals[seloIndex], issue_date: e.target.value };
                                        return {
                                          ...prev,
                                          productionCommercialization: {
                                            ...prev.productionCommercialization,
                                            animal_inspection: { ...prev.productionCommercialization.animal_inspection!, inspection_seals: newSeals },
                                          },
                                        };
                                      });
                                    }}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                              </Grid>
                            )
                          )}
                      </Grid>
                    </Box>
                  )}
                      </>
                    );
                  })()}
                </TabPanel>

                <TabPanel value={tabIndex} index={2}>
                  <Typography variant='h6' gutterBottom>
                    Dados do Informante
                  </Typography>

                  <Grid container spacing={2}>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label='Latitude'
                        fullWidth
                        value={formData?.latitude ?? ''}
                        onChange={(e) =>
                          setFormData((prev) => prev ? { ...prev, latitude: e.target.value } : prev)
                        }
                        type='number'
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label='Longitude'
                        fullWidth
                        value={formData?.longitude ?? ''}
                        onChange={(e) =>
                          setFormData((prev) => prev ? { ...prev, longitude: e.target.value } : prev)
                        }
                        type='number'
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label='Nome do Informante'
                        fullWidth
                        value={formData?.informant_name?.toUpperCase() ?? ''}
                        onChange={(e) =>
                          setFormData((prev) => prev ? { ...prev, informant_name: e.target.value } : prev)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label='CPF do Informante'
                        fullWidth
                        value={mascaraCpfCnpj(formData?.informant_document ?? '')}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => prev ? { ...prev, informant_document: value } : prev);

                          const cleanedValue = value.replace(/\D/g, '');
                          if (cleanedValue.length === 11) {
                            setTimeout(() => {
                              validateCpf(value);
                            }, 100);
                          } else if (cleanedValue.length > 0 && cleanedValue.length < 11) {
                            setCpfError('');
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value) {
                            validateCpf(value);
                          }
                        }}
                        error={!!cpfError}
                        helperText={cpfError}
                        inputProps={{ maxLength: 14 }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="E-mail do Informante"
                        fullWidth
                        value={formData?.informant_email ?? ''}
                        onChange={(e) =>
                          setFormData(prev => (prev ? { ...prev, informant_email: e.target.value } : prev))
                        }
                        error={isInvalidEmail}
                        helperText={isInvalidEmail ? 'E-mail inválido' : ''}
                        type="email"
                        autoComplete="email"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label='Nome do Presidente'
                        fullWidth
                        value={formData?.president_name?.toUpperCase() ?? ''}
                        onChange={(e) =>
                          setFormData((prev) => prev ? { ...prev, president_name: e.target.value } : prev)
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label='Telefone do Informante'
                        fullWidth
                        value={mascaraTelefone(formData?.informant_phone ?? '')}
                        onChange={(e) =>
                          setFormData((prev) => prev ? { ...prev, informant_phone: e.target.value } : prev)
                        }
                        inputProps={{ maxLength: 15 }}
                        type='tel'
                      />
                    </Grid>
                  </Grid>
                </TabPanel>

                <TabPanel value={tabIndex} index={3}>
                  <Typography variant='h6' gutterBottom>
                    Renda e Crédito
                  </Typography>

                  <TextField
                    label='Observações'
                    multiline
                    minRows={3}
                    fullWidth
                    value={formData.observations?.toUpperCase() || ''}
                    onChange={(e) =>
                      setFormData((prev) =>
                        prev ? { ...prev, observations: e.target.value } : prev,
                      )
                    }
                  />
                </TabPanel>
              </>
            )}
          </Paper>
        </ContainerForm>

        {formData && (
          <Box display='flex' justifyContent='flex-end' mt={3}>
            <Button
              variant='contained'
              color='primary'
              onClick={handleSave}
              disabled={!isFormValid()}
            >
              Salvar Alterações
            </Button>
          </Box>
        )}
      </Box>
    </MenuProvider>
  );
};
